import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';

export type DestinationType = 'bank' | 'crypto';

interface DestinationTypeTabsProps {
  value: DestinationType;
  onChange: (value: DestinationType) => void;
  disabled?: boolean;
}

interface TabOption {
  id: DestinationType;
  label: string;
  description: string;
  icon: string;
}

const TABS: TabOption[] = [
  {
    id: 'bank',
    label: 'Bank Account',
    description: 'Withdraw to your linked bank account',
    icon: sfIcons.building2,
  },
  {
    id: 'crypto',
    label: 'External Wallet',
    description: 'Send to an external crypto wallet',
    icon: sfIcons.wallet,
  },
];

/**
 * DestinationTypeTabs - Premium tab switcher for withdrawal destination type
 * 
 * Features:
 * - Smooth selection animations (240ms canon)
 * - Touch-friendly (min 44px height)
 * - Full keyboard navigation
 * - WCAG 2.2 AA compliant
 */
export function DestinationTypeTabs({
  value,
  onChange,
  disabled = false,
}: DestinationTypeTabsProps) {
  return (
    <div
      role="tablist"
      aria-label="Withdrawal destination type"
      className={cn(
        "grid grid-cols-2 gap-3",
        disabled && "opacity-60 pointer-events-none"
      )}
    >
      {TABS.map((tab) => {
        const isSelected = value === tab.id;

        return (
          <button
            key={tab.id}
            role="tab"
            aria-selected={isSelected}
            aria-controls={`${tab.id}-panel`}
            id={`${tab.id}-tab`}
            tabIndex={isSelected ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(tab.id)}
            onKeyDown={(e) => {
              // Arrow key navigation
              if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault();
                const currentIndex = TABS.findIndex(t => t.id === value);
                const nextIndex = e.key === 'ArrowRight' 
                  ? (currentIndex + 1) % TABS.length
                  : (currentIndex - 1 + TABS.length) % TABS.length;
                onChange(TABS[nextIndex].id);
              }
            }}
            className={cn(
              // Base styles
              "relative flex flex-col items-center justify-center gap-2",
              "min-h-[100px] p-4",
              semanticTokens.radius.xl,
              semanticTokens.motion.standard,
              
              // Focus ring (canon: ring-2 ring-inset)
              "focus-visible:outline-none",
              semanticTokens.focus.ring,
              
              // Border & shadow
              "border-2",
              
              // Selected state
              isSelected ? [
                "border-primary bg-primary/5 dark:bg-primary/10",
                "shadow-sm",
              ] : [
                "border-border/50 bg-card hover:bg-muted/50",
                "hover:border-border",
              ],
              
              // Disabled
              disabled && "cursor-not-allowed"
            )}
          >
            {/* Selection indicator dot */}
            {isSelected && (
              <div 
                className={cn(
                  "absolute top-3 right-3",
                  "w-3 h-3 rounded-full",
                  "bg-primary",
                  semanticTokens.motion.fadeIn
                )}
                aria-hidden="true"
              />
            )}
            
            {/* Icon container */}
            <div className={cn(
              "w-12 h-12 rounded-full",
              "flex items-center justify-center",
              semanticTokens.motion.standard,
              isSelected
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground"
            )}>
              <SFIcon icon={tab.icon} className="w-6 h-6" aria-hidden="true" />
            </div>
            
            {/* Label */}
            <span className={cn(
              semanticTokens.typography.label,
              isSelected ? "text-primary" : semanticTokens.fg.default
            )}>
              {tab.label}
            </span>
            
            {/* Description */}
            <span className={cn(
              semanticTokens.typography.caption,
              semanticTokens.fg.muted,
              "text-center max-w-[140px]"
            )}>
              {tab.description}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Compact variant - horizontal pill-style tabs
 */
interface DestinationTypeTabsCompactProps {
  value: DestinationType;
  onChange: (value: DestinationType) => void;
  disabled?: boolean;
}

export function DestinationTypeTabsCompact({
  value,
  onChange,
  disabled = false,
}: DestinationTypeTabsCompactProps) {
  return (
    <div
      role="tablist"
      aria-label="Withdrawal destination type"
      className={cn(
        "inline-flex",
        semanticTokens.bg.muted,
        semanticTokens.radius.lg,
        "p-1 gap-1",
        disabled && "opacity-60 pointer-events-none"
      )}
    >
      {TABS.map((tab) => {
        const isSelected = value === tab.id;

        return (
          <button
            key={tab.id}
            role="tab"
            aria-selected={isSelected}
            disabled={disabled}
            onClick={() => onChange(tab.id)}
            className={cn(
              "flex items-center gap-2",
              "px-4 py-2 min-h-[44px]",
              semanticTokens.radius.md,
              semanticTokens.motion.standard,
              semanticTokens.typography.label,
              "focus-visible:outline-none",
              semanticTokens.focus.ring,

              isSelected ? [
                semanticTokens.bg.surface,
                semanticTokens.fg.default,
                semanticTokens.shadow.sm,
              ] : [
                "bg-transparent",
                semanticTokens.fg.muted,
                "hover:text-foreground",
              ]
            )}
          >
            <SFIcon icon={tab.icon} className="w-4 h-4" aria-hidden="true" />
            <span>{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}

